import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createManufacturingMaterialUse,
  getManufacturingMaterialUseDetail,
  listManufacturingMaterialUses,
  updateManufacturingMaterialUse,
} from "@/lib/manufacturing/materialUsesClient";
import type {
  MfgMaterialUse,
  MfgMaterialUseMember,
  MfgMaterialUseResolveMode,
} from "@/lib/manufacturing/types";
import { useMaterialUseMemberActions } from "./useMaterialUseMemberActions";

function useCodeFromName(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return slug.slice(0, 48) || "use";
}

export function useManufacturingMaterialUsesTab(
  enabled: boolean,
  refreshTick: number,
  plantId: string | null
) {
  const [uses, setUses] = useState<MfgMaterialUse[]>([]);
  const [memberSearchByUseId, setMemberSearchByUseId] = useState<Record<string, string>>({});
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [members, setMembers] = useState<MfgMaterialUseMember[]>([]);
  const [variantOnHandQty, setVariantOnHandQty] = useState<number | null>(null);
  const [openDeficitCount, setOpenDeficitCount] = useState(0);
  const [listBusy, setListBusy] = useState(false);
  const [detailBusy, setDetailBusy] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [newName, setNewName] = useState("");
  const [newUnit, setNewUnit] = useState("each");
  const [newNotes, setNewNotes] = useState("");
  const [newResolveMode, setNewResolveMode] = useState<MfgMaterialUseResolveMode>("substitute");
  const [newFinishChannel, setNewFinishChannel] = useState("siding");
  const [newCodeTouched, setNewCodeTouched] = useState(false);

  const [editCode, setEditCode] = useState("");
  const [editName, setEditName] = useState("");
  const [editUnit, setEditUnit] = useState("each");
  const [editNotes, setEditNotes] = useState("");
  const [editActive, setEditActive] = useState(true);
  const [editResolveMode, setEditResolveMode] =
    useState<MfgMaterialUseResolveMode>("substitute");
  const [editFinishChannel, setEditFinishChannel] = useState("");

  const reloadList = useCallback(async () => {
    if (!enabled) return;
    setListBusy(true);
    setLocalError(null);
    const r = await listManufacturingMaterialUses();
    setListBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    setUses(
      r.data.uses.map((u) => ({
        ...u,
        resolve_mode: u.resolve_mode === "finish_variant" ? "finish_variant" : "substitute",
        finish_channel: u.finish_channel ?? null,
      })),
    );
    setMemberSearchByUseId(r.data.member_search_by_use_id ?? {});
  }, [enabled]);

  const reloadDetail = useCallback(
    async (id: string) => {
      setDetailBusy(true);
      setLocalError(null);
      const r = await getManufacturingMaterialUseDetail(id, {
        plant_id: plantId ?? undefined,
      });
      setDetailBusy(false);
      if (!r.ok) {
        setLocalError(r.error);
        return;
      }
      const use = r.data.use;
      setMembers(
        r.data.members.map((m) => ({
          ...m,
          is_primary: m.is_primary === true,
          finish_code: m.finish_code ?? null,
        })),
      );
      setVariantOnHandQty(
        typeof r.data.variant_on_hand_qty === "number" ? r.data.variant_on_hand_qty : null,
      );
      setOpenDeficitCount(
        typeof r.data.open_deficit_count === "number" ? r.data.open_deficit_count : 0,
      );
      setEditCode(use.use_code);
      setEditName(use.name);
      setEditUnit(use.unit);
      setEditNotes(use.notes);
      setEditActive(use.is_active);
      setEditResolveMode(use.resolve_mode === "finish_variant" ? "finish_variant" : "substitute");
      setEditFinishChannel(use.finish_channel ?? "");
    },
    [plantId]
  );

  useEffect(() => {
    void reloadList();
  }, [reloadList, refreshTick]);

  useEffect(() => {
    if (!selectedId) {
      setMembers([]);
      setVariantOnHandQty(null);
      setOpenDeficitCount(0);
      return;
    }
    void reloadDetail(selectedId);
  }, [selectedId, reloadDetail, refreshTick, plantId]);

  // Keep list material-search haystack fresh after member add/remove on the open use.
  useEffect(() => {
    if (!selectedId) return;
    const hay = members
      .filter((m) => m.is_active)
      .map((m) => `${m.material_code} ${m.material_name}`.trim())
      .filter(Boolean)
      .join(" ");
    setMemberSearchByUseId((prev) => {
      if ((prev[selectedId] ?? "") === hay) return prev;
      return { ...prev, [selectedId]: hay };
    });
  }, [selectedId, members]);

  const pickUse = useCallback((id: string) => {
    setCreating(false);
    setSelectedId(id);
  }, []);

  const beginCreate = useCallback(() => {
    setSelectedId(null);
    setCreating(true);
    setNewCode("");
    setNewName("");
    setNewUnit("each");
    setNewNotes("");
    setNewResolveMode("substitute");
    setNewFinishChannel("siding");
    setNewCodeTouched(false);
    setMembers([]);
  }, []);

  const closeEditor = useCallback(() => {
    setCreating(false);
    setSelectedId(null);
    setMembers([]);
  }, []);

  const selectedUse = useMemo(
    () => (selectedId ? uses.find((u) => u.id === selectedId) ?? null : null),
    [selectedId, uses]
  );

  const createDirty = creating && (newName.trim().length > 0 || newCode.trim().length > 0);
  const editDirty = useMemo(() => {
    if (!selectedUse) return false;
    return (
      editName !== selectedUse.name ||
      editCode !== selectedUse.use_code ||
      editUnit !== selectedUse.unit ||
      editNotes !== selectedUse.notes ||
      editActive !== selectedUse.is_active ||
      editResolveMode !== (selectedUse.resolve_mode ?? "substitute") ||
      editFinishChannel !== (selectedUse.finish_channel ?? "")
    );
  }, [
    selectedUse,
    editName,
    editCode,
    editUnit,
    editNotes,
    editActive,
    editResolveMode,
    editFinishChannel,
  ]);

  const setNewNameAndMaybeCode = useCallback(
    (name: string) => {
      setNewName(name);
      if (!newCodeTouched) setNewCode(useCodeFromName(name));
    },
    [newCodeTouched]
  );

  const saveCreate = useCallback(async () => {
    setSaveBusy(true);
    setLocalError(null);
    const r = await createManufacturingMaterialUse({
      use_code: newCode,
      name: newName,
      unit: newUnit,
      notes: newNotes,
      resolve_mode: newResolveMode,
      finish_channel: newResolveMode === "finish_variant" ? newFinishChannel : null,
    });
    setSaveBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return { ok: false as const, error: r.error };
    }
    setCreating(false);
    setSelectedId(r.data.use.id);
    await reloadList();
    return { ok: true as const, use: r.data.use };
  }, [newCode, newFinishChannel, newName, newNotes, newResolveMode, newUnit, reloadList]);

  const saveEdit = useCallback(async () => {
    if (!selectedId) return { ok: false as const, error: "No use selected." };
    setSaveBusy(true);
    setLocalError(null);
    const r = await updateManufacturingMaterialUse({
      use_id: selectedId,
      use_code: editCode,
      name: editName,
      unit: editUnit,
      notes: editNotes,
      is_active: editActive,
      resolve_mode: editResolveMode,
      finish_channel: editResolveMode === "finish_variant" ? editFinishChannel || null : null,
    });
    setSaveBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return { ok: false as const, error: r.error };
    }
    await reloadList();
    await reloadDetail(selectedId);
    return { ok: true as const };
  }, [
    editActive,
    editCode,
    editFinishChannel,
    editName,
    editNotes,
    editResolveMode,
    editUnit,
    reloadDetail,
    reloadList,
    selectedId,
  ]);

  const { addMember, addMembers, updateMember, removeMember } = useMaterialUseMemberActions({
    selectedId,
    members,
    editResolveMode,
    reloadDetail,
    setSaveBusy,
    setLocalError,
  });

  return {
    uses,
    memberSearchByUseId,
    members,
    variantOnHandQty,
    openDeficitCount,
    selectedId,
    selectedUse,
    creating,
    listBusy,
    detailBusy,
    saveBusy,
    localError,
    setLocalError,
    newCode,
    setNewCode,
    setNewCodeTouched,
    newName,
    setNewName: setNewNameAndMaybeCode,
    newUnit,
    setNewUnit,
    newNotes,
    setNewNotes,
    newResolveMode,
    setNewResolveMode,
    newFinishChannel,
    setNewFinishChannel,
    editCode,
    setEditCode,
    editName,
    setEditName,
    editUnit,
    setEditUnit,
    editNotes,
    setEditNotes,
    editActive,
    setEditActive,
    editResolveMode,
    setEditResolveMode,
    editFinishChannel,
    setEditFinishChannel,
    pickUse,
    beginCreate,
    closeEditor,
    saveCreate,
    saveEdit,
    addMember,
    addMembers,
    updateMember,
    removeMember,
    reloadList,
    createDirty,
    editDirty,
  };
}
